import { useEffect, useReducer, useRef, useState } from 'react';
import { gameReducer } from '../state/reducer.js';
import { createInitialState } from '../state/initialState.js';
import { PHASE } from '../state/phases.js';
import { TitleScreen } from '../screens/TitleScreen.jsx';
import { SubsidyRevealScreen } from '../screens/SubsidyRevealScreen.jsx';
import { BuildScreen } from '../screens/BuildScreen.jsx';
import { YearTransitionScreen } from '../screens/YearTransitionScreen.jsx';
import { GridPerformanceReportScreen } from '../screens/GridPerformanceReportScreen.jsx';
import { Cop30Screen } from '../screens/Cop30Screen.jsx';
import { Cop30TransitionScreen } from '../screens/Cop30TransitionScreen.jsx';
import { FinalScoreScreen } from '../screens/FinalScoreScreen.jsx';
import { RulesScreen } from '../screens/RulesScreen.jsx';
import { startGame, acknowledgeSubsidy } from '../state/actions.js';
import { isAdvancedMode } from '../state/modeConfig.js';

function pickInitialSeed() {
  return ((Math.random() * 0xffffffff) >>> 0) || 1;
}

export function GameView({
  playerName,
  playerCountry,
  playerRole,
  gameMode,
  rulesOpen = false,
  onRulesClose,
  onNavigate
}) {
  const [state, dispatch] = useReducer(
    gameReducer,
    undefined,
    () => createInitialState({ seed: pickInitialSeed(), mode: gameMode })
  );
  const [showRules, setShowRules] = useState(false);
  const [transitionRunning, setTransitionRunning] = useState(false);
  const [cop30TransitionRunning, setCop30TransitionRunning] = useState(false);
  const lastGameplayScreenRef = useRef(null);

  const [gameStarted, setGameStarted] = useState(false);
  if (!gameStarted && state.phase === PHASE.AWAITING_START) {
    dispatch(startGame());
    setGameStarted(true);
  }

  const [autoAckedYear, setAutoAckedYear] = useState(0);
  if (
    state.phase === PHASE.AWAITING_SUBSIDY_REVEAL &&
    state.year > 1 &&
    autoAckedYear !== state.year
  ) {
    dispatch(acknowledgeSubsidy());
    setAutoAckedYear(state.year);
  }

  useEffect(() => {
    if (state.phase === PHASE.AWAITING_VARIABLE_ROLLS && !transitionRunning) {
      setTransitionRunning(true);
    }
  }, [state.phase, transitionRunning]);

  function renderScreen() {
    switch (state.phase) {
      case PHASE.AWAITING_START:
        return <TitleScreen dispatch={dispatch} onShowRules={() => setShowRules(true)} />;
      case PHASE.AWAITING_SUBSIDY_REVEAL:
        return <SubsidyRevealScreen state={state} dispatch={dispatch} />;
      case PHASE.BUILDING:
        return <BuildScreen state={state} dispatch={dispatch} />;
      case PHASE.AWAITING_VARIABLE_ROLLS:
      case PHASE.AWAITING_DISASTER:
      case PHASE.RESOLVING:
        return null;
      case PHASE.BETWEEN_YEARS:
        return <GridPerformanceReportScreen state={state} dispatch={dispatch} />;
      case PHASE.COP30:
        return (
          <Cop30Screen
            state={state}
            dispatch={dispatch}
            onReveal={() => setCop30TransitionRunning(true)}
            outcomeAudioEnabled={!cop30TransitionRunning}
          />
        );
      case PHASE.GAME_OVER:
        return (
          <FinalScoreScreen
            state={state}
            dispatch={dispatch}
            playerName={playerName}
            playerCountry={playerCountry}
            playerRole={playerRole}
            onNavigate={onNavigate}
          />
        );
      default:
        return null;
    }
  }

  const showAnyRules = showRules || rulesOpen;

  function closeRules() {
    setShowRules(false);
    onRulesClose?.();
  }

  const currentScreen = showAnyRules ? (
    <RulesScreen onClose={closeRules} advanced={isAdvancedMode(state)} />
  ) : (
    renderScreen()
  );
  const usingPreservedUnderlay =
    !showAnyRules && currentScreen === null && lastGameplayScreenRef.current;
  const visibleScreen = usingPreservedUnderlay
    ? lastGameplayScreenRef.current
    : currentScreen;
  const transitionActive =
    transitionRunning || cop30TransitionRunning || usingPreservedUnderlay;

  if (!showAnyRules && currentScreen !== null) {
    lastGameplayScreenRef.current = currentScreen;
  }

  return (
    <>
      <div
        className={transitionActive ? 'game-transition-underlay' : undefined}
        aria-hidden={transitionActive ? 'true' : undefined}
        inert={transitionActive ? '' : undefined}
      >
        {visibleScreen}
      </div>
      {transitionRunning ? (
        <YearTransitionScreen
          state={state}
          dispatch={dispatch}
          onComplete={() => setTransitionRunning(false)}
        />
      ) : null}
      {cop30TransitionRunning ? (
        <Cop30TransitionScreen
          state={state}
          dispatch={dispatch}
          onComplete={() => setCop30TransitionRunning(false)}
        />
      ) : null}
    </>
  );
}
